import { useCallback, useMemo } from "react";
import { Box, Stack, Typography, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import type { PlotMouseEvent } from "plotly.js";
import type { JobFailureWindow } from "src/store/api/generatedApi";
import { useResolvedScrapViewportTimeZone } from "store/slices/scrapTrackingViewportSlice";
import {
  buildStackedBarTraces,
  formatHoverTimeRange,
  stackedChartLayout,
  timelineChartConfig,
  TimelineLegend,
} from "../components/timelineChartUtils";

type JobFailuresTimelineProps = Readonly<{
  windows: readonly JobFailureWindow[];
  start: Date;
  end: Date;
  onWindowClick?: (window: JobFailureWindow) => void;
}>;

const TERMINAL_FAILURE_COLOR = "#d32f2f";
const RETRIABLE_FAILURE_COLOR = "#ffa726";
const NO_FAILURES_COLOR = "#66bb6a";
const CLICK_HINT_COLOR = "#4441C8";

export interface FailureBands {
  healthy: number;
  retriable: number;
  terminal: number;
}

export const computeFailureBands = (window: JobFailureWindow): FailureBands => {
  if (window.total_batches <= 0) {
    return { healthy: 0, retriable: 0, terminal: 0 };
  }
  const terminal = Math.min(
    1,
    window.terminal_failure_count / window.total_batches
  );
  const retriable = Math.min(
    1 - terminal,
    window.retriable_failure_count / window.total_batches
  );
  const healthy = Math.max(0, 1 - terminal - retriable);
  return { healthy, retriable, terminal };
};

const STAGE_CONFIG = [
  { key: "healthy" as const, color: NO_FAILURES_COLOR, name: "Healthy" },
  {
    key: "retriable" as const,
    color: RETRIABLE_FAILURE_COLOR,
    name: "Retriable",
  },
  { key: "terminal" as const, color: TERMINAL_FAILURE_COLOR, name: "Terminal" },
] as const;

const failureHoverText = (
  window: JobFailureWindow,
  timeZone: string,
  clickHint: string | null
): string => {
  const lines = [
    `<b>${formatHoverTimeRange(window.window_start, window.window_end, timeZone)}</b>`,
    `Terminal failures: ${window.terminal_failure_count} / ${window.total_batches} batches`,
    `Retriable failures: ${window.retriable_failure_count} / ${window.total_batches} batches`,
  ];
  if (clickHint && window.terminal_failure_count > 0) {
    lines.push(
      `<span style="color:${CLICK_HINT_COLOR}"><i>${clickHint}</i></span>`
    );
  }
  return lines.join("<br>");
};

export const JobFailuresTimeline = ({
  windows,
  start,
  end,
  onWindowClick,
}: JobFailuresTimelineProps) => {
  const { t } = useTenantTranslation();
  const timeZone = useResolvedScrapViewportTimeZone();
  const theme = useTheme();

  const clickHint = onWindowClick ? t("resetTerminalFailuresClickHint") : null;
  const lineColor = theme.palette.background.default;
  const traces = useMemo(
    () =>
      buildStackedBarTraces({
        windows,
        stageConfig: STAGE_CONFIG,
        computeBands: computeFailureBands,
        hoverTextFn: (window) => failureHoverText(window, timeZone, clickHint),
        lineColor,
      }),
    [windows, timeZone, lineColor, clickHint]
  );
  const layout = useMemo(
    () => stackedChartLayout(start, end, theme),
    [start, end, theme]
  );

  const handleClick = useCallback(
    (event: Readonly<PlotMouseEvent>) => {
      const pointNumber = event.points[0]?.pointNumber;
      if (pointNumber === undefined || !onWindowClick) return;
      const clicked = windows[pointNumber];
      if (clicked) onWindowClick(clicked);
    },
    [windows, onWindowClick]
  );

  return (
    <Stack spacing={0.5}>
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="subtitle2">{t("jobFailuresTimeline")}</Typography>
        <TimelineLegend
          color={TERMINAL_FAILURE_COLOR}
          label={t("jobFailureTerminal")}
        />
        <TimelineLegend
          color={RETRIABLE_FAILURE_COLOR}
          label={t("jobFailureRetriable")}
        />
        <TimelineLegend color={NO_FAILURES_COLOR} label={t("jobFailureNone")} />
      </Stack>
      <Box sx={{ width: "100%" }}>
        <ScrapTrackingViewportPlot
          data={traces}
          layout={layout}
          config={timelineChartConfig}
          style={{
            width: "100%",
            cursor: onWindowClick ? "pointer" : undefined,
          }}
          onClick={handleClick}
        />
      </Box>
    </Stack>
  );
};
